import type { ReactElement } from "react";

import { ChevronDown, ChevronRight } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Combobox } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import type { RegionGroup } from "app/pages/sales-intelligence/utils/location";
import { getRegionState, toggleCountry, toggleRegion } from "app/pages/sales-intelligence/utils/location";
import type { ICountryObject } from "app/services/CountryService";

import { CountryOption } from "./CountryOption";
import { getRegionRowPadding } from "./rowIndent";
import type { VisibleRegionGroup } from "./types";
import { countVisibleNestedCountries } from "./utils";

export type LocationGroupedRegionSectionProps = {
    group: RegionGroup;
    filteredCountries: readonly ICountryObject[];
    filteredSubGroups: readonly VisibleRegionGroup[];
    selectedCodes: ReadonlySet<number>;
    dataAutomation: string;
    searchActive: boolean;
    depth?: number;
    isRegionExpanded: (regionName: string) => boolean;
    onChange: (codes: readonly number[]) => void;
    toggleExpanded: (regionName: string) => void;
};

export const LocationGroupedRegionSection = ({
    group,
    filteredCountries,
    filteredSubGroups,
    selectedCodes,
    dataAutomation,
    searchActive,
    depth = 0,
    isRegionExpanded,
    onChange,
    toggleExpanded,
}: LocationGroupedRegionSectionProps): ReactElement => {
    const { region } = group;
    const expandedNow = isRegionExpanded(region.name);
    const regionLabel = region.text ?? region.name;
    const memberIds = group.memberCountries.map((country) => country.id);
    const regionState = getRegionState(memberIds, selectedCodes);
    const visibleSubtree: VisibleRegionGroup = {
        group,
        filteredCountries,
        filteredSubGroups,
    };
    const displayCount = searchActive ? countVisibleNestedCountries(visibleSubtree) : group.memberCountries.length;
    const countryDepth = depth + 1;
    const hasExpandableChildren = filteredSubGroups.length > 0 || filteredCountries.length > 0;

    const regionRow = (
        <Combobox.Option
            id={`region-${region.name}`}
            textValue={`${regionLabel} (${displayCount})`}
            data-automation={`${dataAutomation}-region-row-${region.name}`}
        >
            <Flex
                alignItems="center"
                justifyContent="space-between"
                gap="8px"
                width="100%"
                padding={getRegionRowPadding(depth)}
            >
                <Flex
                    alignItems="center"
                    gap="4px"
                    flex="1"
                    minWidth={0}
                >
                    <Flex
                        flexShrink={0}
                        alignItems="center"
                        justifyContent="center"
                        padding="4px"
                        aria-hidden={!hasExpandableChildren}
                    >
                        {hasExpandableChildren ? (
                            <Flex
                                as="button"
                                type="button"
                                alignItems="center"
                                justifyContent="center"
                                cursor="pointer"
                                border="none"
                                background="none"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    toggleExpanded(region.name);
                                }}
                                data-automation={`${dataAutomation}-region-chevron-${region.name}`}
                                aria-expanded={expandedNow}
                                aria-label={
                                    expandedNow
                                        ? translate("si.lead_gen_filters.countries.region_collapse_aria")
                                        : translate("si.lead_gen_filters.countries.region_expand_aria")
                                }
                            >
                                {expandedNow ? (
                                    <ChevronDown
                                        size="xs"
                                        color={colorsPalettes.carbon["300"]}
                                    />
                                ) : (
                                    <ChevronRight
                                        size="xs"
                                        color={colorsPalettes.carbon["300"]}
                                    />
                                )}
                            </Flex>
                        ) : null}
                    </Flex>
                    <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        gap="6px"
                        flex="1"
                        minWidth={0}
                        data-automation={`${dataAutomation}-region-${region.name}`}
                    >
                        <Combobox.OptionLabel>{regionLabel}</Combobox.OptionLabel>
                        <Text
                            fontVariant="caption"
                            color={colorsPalettes.carbon["300"]}
                        >
                            {displayCount}
                        </Text>
                    </Flex>
                </Flex>
                <Flex
                    alignItems="center"
                    onClick={(e) => {
                        e.stopPropagation();
                        onChange(toggleRegion(memberIds, selectedCodes));
                    }}
                    data-automation={`${dataAutomation}-region-checkbox-${region.name}`}
                >
                    <Combobox.OptionCheckbox
                        isChecked={regionState === "checked"}
                        halfSelected={regionState === "partial"}
                    />
                </Flex>
            </Flex>
        </Combobox.Option>
    );

    const expandedContent = expandedNow
        ? [
              ...filteredSubGroups.map((visibleSubGroup) => (
                  <LocationGroupedRegionSection
                      key={visibleSubGroup.group.region.name}
                      group={visibleSubGroup.group}
                      filteredCountries={visibleSubGroup.filteredCountries}
                      filteredSubGroups={visibleSubGroup.filteredSubGroups}
                      selectedCodes={selectedCodes}
                      dataAutomation={dataAutomation}
                      searchActive={searchActive}
                      depth={depth + 1}
                      isRegionExpanded={isRegionExpanded}
                      onChange={onChange}
                      toggleExpanded={toggleExpanded}
                  />
              )),
              ...filteredCountries.map((countryItem) => {
                  const countryOptionId = `country-${region.name}-${countryItem.id}`;

                  return (
                      <CountryOption
                          key={countryOptionId}
                          countryItem={countryItem}
                          selectedCodes={selectedCodes}
                          dataAutomation={`${dataAutomation}-country-${countryItem.id}`}
                          optionId={countryOptionId}
                          depth={countryDepth}
                          onSelectCountry={() => onChange(toggleCountry(countryItem.id, selectedCodes))}
                      />
                  );
              }),
          ]
        : null;

    if (depth === 0) {
        return (
            <Combobox.Section>
                {regionRow}
                {expandedContent}
            </Combobox.Section>
        );
    }

    return (
        <>
            {regionRow}
            {expandedContent}
        </>
    );
};
